import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import { publicApi } from '../lib/api';

const STORAGE_KEY = 'resawork-last-seen-version';

// Simple semver compare: returns true if a > b (e.g. "5.9.0" > "5.7.2")
function isNewer(a, b) {
  if (!a || !b) return !!a && a !== b;
  const pa = a.split('.').map(n => parseInt(n, 10) || 0);
  const pb = b.split('.').map(n => parseInt(n, 10) || 0);
  for (let i = 0; i < 3; i++) {
    if ((pa[i] || 0) > (pb[i] || 0)) return true;
    if ((pa[i] || 0) < (pb[i] || 0)) return false;
  }
  return false;
}

// Toast that appears in the bottom-right corner once per logged-in user
// when the app version has changed since their last visit.
//
// Behavior:
//   - On mount, fetch /api/app/version
//   - Compare with localStorage `resawork-last-seen-version`
//   - If newer, show the toast (auto-hide after 12s OR manual close)
//   - On close OR click "Voir les nouveautés", store the new version locally
//
// Usage: drop <NewVersionToast /> at the root of any layout that renders for
// logged-in users (CompanyDashboard, ClientDashboard, CollaboratorDashboard).
export default function NewVersionToast() {
  const [version, setVersion] = useState(null);
  const [show, setShow] = useState(false);
  const mountedRef = useRef(true);
  const timeoutRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      try {
        const res = await publicApi.getAppVersion();
        const remote = res.data?.version;
        if (!remote) return;
        const lastSeen = localStorage.getItem(STORAGE_KEY);
        if (!lastSeen) {
          // First visit ever — silently set it without showing the toast
          localStorage.setItem(STORAGE_KEY, remote);
          return;
        }
        if (isNewer(remote, lastSeen) && mountedRef.current) {
          setVersion(remote);
          setShow(true);
          // Auto-dismiss after 12s
          timeoutRef.current = setTimeout(() => handleDismiss(remote), 12000);
        }
      } catch {}
    })();
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDismiss = (v) => {
    try {
      if (v) localStorage.setItem(STORAGE_KEY, v);
      else if (version) localStorage.setItem(STORAGE_KEY, version);
    } catch {}
    setShow(false);
  };

  if (!show || !version) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-[10001] max-w-sm animate-slide-in-right"
      data-testid="new-version-toast"
      role="status"
      aria-live="polite"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-primary/20 overflow-hidden">
        <div className="bg-gradient-to-r from-primary to-emerald-500 h-1" />
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-primary animate-pulse" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-primary uppercase tracking-wider">
                  Nouvelle version
                </p>
                <button
                  onClick={() => handleDismiss(version)}
                  aria-label="Fermer"
                  className="p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="new-version-toast-close"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <h4 className="font-bold text-slate-900 text-sm leading-tight">
                ResaWork v{version} disponible
              </h4>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                Découvrez les nouvelles fonctionnalités ajoutées récemment :
                planning drag & drop, export PDF par membre, guides interactifs…
              </p>
              <Link
                to="/changelog"
                onClick={() => handleDismiss(version)}
                data-testid="new-version-toast-cta"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors group"
              >
                Voir les nouveautés
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes slide-in-right {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-slide-in-right { animation: slide-in-right 0.4s cubic-bezier(0.4, 0, 0.2, 1); }
      `}</style>
    </div>
  );
}
